import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem } from "@/components/ui/dropdown-menu";
import { LogOut, Wifi, WifiOff, ChevronDown } from "lucide-react";

const ROLE_LABEL = {
  owner: "Owner", manager: "Manager Cabang", kasir: "Kasir", dapur: "Admin Dapur", member: "Member", keuangan: "Staf Keuangan",
};
const FACTORS = [0.25, 0.5, 0.75, 1.0];
export const DISPLAY_FACTOR_EVENT = "jjl-display-factor";

function FactorDropdown() {
  const [factor, setFactor] = useState(1.0);
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/settings/display-factor"); setFactor(data.factor); } catch (e) { } })();
    const onEvt = (e) => setFactor(e.detail);
    window.addEventListener(DISPLAY_FACTOR_EVENT, onEvt);
    return () => window.removeEventListener(DISPLAY_FACTOR_EVENT, onEvt);
  }, []);
  const changeFactor = async (f) => {
    setFactor(f);
    window.dispatchEvent(new CustomEvent(DISPLAY_FACTOR_EVENT, { detail: f }));
    try { await api.put("/settings/display-factor", { factor: f }); toast.success(`Porsi tampilan omset ${Math.round(f * 100)}%`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="factor-menu-trigger" className="flex items-center gap-1.5 rounded-full bg-crimson/10 px-3 py-1.5 text-xs font-bold text-crimson transition-colors hover:bg-crimson/20">
          {Math.round(factor * 100)}% <ChevronDown size={13} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-32">
        <DropdownMenuRadioGroup value={String(factor)} onValueChange={(v) => changeFactor(parseFloat(v))}>
          {FACTORS.map((f) => (
            <DropdownMenuRadioItem key={f} data-testid={`factor-${Math.round(f * 100)}`} value={String(f)}>{Math.round(f * 100)}%</DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TopBar({ children, showFactor = false }) {
  const { user, logout } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  return (
    <header className="glass sticky top-0 z-40 border-b border-neutral-200">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-4 sm:px-6">
        <Brand size={40} />
        <div className="flex items-center gap-2 sm:gap-3">
          {children}
          {showFactor && user?.role === "owner" && <FactorDropdown />}
          <div
            data-testid="offline-badge"
            className={`hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold sm:flex ${
              online ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"
            }`}
          >
            {online ? <Wifi size={13} /> : <WifiOff size={13} />}
            {online ? "Online" : "Offline"}
          </div>
          <div className="hidden text-right sm:block">
            <div className="text-sm font-semibold text-neutral-900">{user?.name}</div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-gold">{ROLE_LABEL[user?.role]}</div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-muted-foreground hover:text-crimson hover:bg-crimson/10"
          >
            <LogOut size={18} />
          </Button>
        </div>
      </div>
    </header>
  );
}
